import axios, { AxiosInstance } from 'axios';
import { getApiBaseUrl, isAdminPath } from '../utils/portal';
import { queryClient } from '../queryClient';

/**
 * Create axios instance with default configuration
 */
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: getApiBaseUrl(),
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  // Response interceptor for error handling and session management
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      // Handle 401 - invalid/expired session
      if (error.response?.status === 401) {
        // Clear session data from sessionStorage
        sessionStorage.removeItem('vendorSession');
        sessionStorage.removeItem('credentialId');
        localStorage.removeItem('vendorSession');
        // Redirect to the login page of whichever portal we are in
        const currentPath = window.location.pathname;
        if (!currentPath.endsWith('/login')) {
          window.location.href = isAdminPath(currentPath) ? '/admin/login' : '/login';
        }
      }
      // Handle 403 - forbidden (e.g., credential blocked)
      if (error.response?.status === 403) {
        sessionStorage.removeItem('vendorSession');
        sessionStorage.removeItem('credentialId');
        localStorage.removeItem('vendorSession');
        // In the admin portal a 403 means "read-only staff", not "session gone"
        if (!isAdminPath()) {
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    }
  );

  return client;
};

export const api = createApiClient();

// Admin API endpoints
export const adminApi = {
  // Authentication - Entra sign-in is a full-page navigation to the backend,
  // which redirects to Microsoft and back to returnTo
  signInUrl: (returnTo = '/admin/dashboard') =>
    `${getApiBaseUrl()}/admin/auth/login?returnTo=${encodeURIComponent(returnTo)}`,

  logout: () =>
    api.post<{ signOutUrl: string }>('/admin/auth/logout'),

  me: () =>
    api.get('/admin/auth/me'),

  // Tenant management
  getTenants: (includeInactive = false) =>
    api.get('/admin/tenants', { params: { includeInactive } }),

  getTenant: (id: number) =>
    api.get(`/admin/tenants/${id}`),

  createTenant: (tenantData: any) =>
    api.post('/admin/tenants', tenantData),

  updateTenant: (id: number, tenantData: any) =>
    api.put(`/admin/tenants/${id}`, tenantData),

  deleteTenant: (id: number) =>
    api.delete(`/admin/tenants/${id}`),

  // Vendor logins synced from the tenant's Business Central
  getTenantVendors: (id: number) =>
    api.get(`/admin/tenants/${id}/vendors`),

  syncTenantVendors: (id: number) =>
    api.post(`/admin/tenants/${id}/sync-vendors`),

  // Key Business Central uses to push vendor login changes - returned once
  createIntegrationKey: (id: number) =>
    api.post<{ key: string; createdAt: string }>(`/admin/tenants/${id}/integration-key`)
};

// Vendor API endpoints
export const vendorApi = {
  // Authentication
  // login is a username or email. If it exists at several companies/vendors the
  // response has needsSelection + options; resubmit with the chosen one.
  login: (login: string, password: string, selection?: { tenantId: number; vendorNo: string }) =>
    api.post('/vendor/auth/login', { login, password, ...selection }).then((response) => {
      // Store session credentials for session management
      if (response.data.vendor?.credentialId) {
        // A new user is signed in: drop everything cached for the previous one
        queryClient.clear();
        sessionStorage.setItem('credentialId', response.data.vendor.credentialId);
        sessionStorage.setItem('vendorSession', JSON.stringify({
          vendorNo: response.data.vendor.vendorNo,
          username: response.data.vendor.username,
          credentialId: response.data.vendor.credentialId,
          fullName: response.data.vendor.fullName,
          email: response.data.vendor.email,
          loginTime: new Date().toISOString()
        }));
      }
      return response;
    }),

  logout: () => {
    // Clear session data
    sessionStorage.removeItem('credentialId');
    sessionStorage.removeItem('vendorSession');
    localStorage.removeItem('vendorSession');
    queryClient.clear();
    return api.post('/vendor/auth/logout');
  },

  me: () =>
    api.get('/vendor/auth/me'),


  // Data
  getDashboardData: () =>
    api.get('/vendor/dashboard-data'),

  getProfile: () =>
    api.get('/vendor/profile'),

  getResources: () =>
    api.get('/vendor/resources'),

  getInvoices: (params?: any) =>
    api.get('/vendor/invoices', { params }),

  /** documentType: 'invoice' | 'creditMemo' */
  getInvoiceDetails: (documentType: string, documentNo: string) =>
    api.get(`/vendor/invoices/${documentType}/${encodeURIComponent(documentNo)}`),

  getInvoicePdf: (documentType: string, documentNo: string) =>
    api.get(`/vendor/invoices/${documentType}/${encodeURIComponent(documentNo)}/pdf`, { responseType: 'blob', timeout: 120000 }),

  /** The settlement as BC's Excel export (needs Settled.Reports in BC) */
  getInvoiceExcel: (documentType: string, documentNo: string) =>
    api.get(`/vendor/invoices/${documentType}/${encodeURIComponent(documentNo)}/excel`, { responseType: 'blob', timeout: 120000 }),

  /** view: 'open' (next settlement) | 'held' (late / credit risk) | 'settled' (on a settlement invoice) */
  getSettlementOrders: (params?: { view?: string; $top?: number; $skip?: number }) =>
    api.get('/vendor/settlement-orders', { params }),

  getSettlementOrderDetails: (id: string) =>
    api.get(`/vendor/settlement-orders/${id}`),

  /** view: 'open' (still to be deducted) | 'deducted' | 'all' */
  getSettlementExpenses: (view = 'open') =>
    api.get('/vendor/settlement-expenses', { params: { view } }),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/vendor/auth/change-password', { currentPassword, newPassword }),

  getUsers: () =>
    api.get('/vendor/users'),

  getOverview: () =>
    api.get('/vendor/overview'),

  getDocumentAttachments: (documentId: string) =>
    api.get(`/vendor/documents/${documentId}/attachments`),

  getDocumentAttachment: (documentId: string, attachmentId: number) =>
    api.get(`/vendor/documents/${documentId}/attachments/${attachmentId}`, { responseType: 'blob', timeout: 120000 }),

  getFixedCosts: () =>
    api.get('/vendor/fixed-costs'),

  getCustomers: (params?: any) =>
    api.get('/vendor/customers', { params }),

  getCustomerDetails: (customerNo: string) =>
    api.get(`/vendor/customers/${encodeURIComponent(customerNo)}`),

  getResourceDetails: (id: string) =>
    api.get(`/vendor/resources/${id}`),

  getTenantConfig: () =>
    api.get('/vendor/tenant-config'),

  getNews: () =>
    api.get('/vendor/news')
};

export default api;
